import React, { Component } from 'react';
import { Box, Callout, Heading, Text } from 'core/components';
import { DataGrid, DataRow, ExternalLink, PolicyLocations } from 'app/views/core/details/asns/peeringDataDisplay';
import PolicyType from 'app/views/core/details/asns/PolicyType';
import Markdown from 'app/components/markdown/Markdown';
import ToolResultCard from '../components/ToolResultCard';

const PEERINGDB_BASE = 'https://www.peeringdb.com';

function pdbAsnUrl(asn) {
  return asn ? `${PEERINGDB_BASE}/asn/${asn}` : null;
}

function pdbIxUrl(id) {
  return id ? `${PEERINGDB_BASE}/ix/${id}` : null;
}

function pdbFacUrl(id) {
  return id ? `${PEERINGDB_BASE}/fac/${id}` : null;
}

function asnDetailsUrl(asn) {
  return asn ? `/v4/core/quick-views/asn/${asn}/peeringdb` : null;
}

function formatLocation({ city, country, continent, region_continent: regionContinent }) {
  return [city, country, continent || regionContinent].filter(Boolean).join(', ');
}

// =============================================================================
// Markdown table helpers
// =============================================================================

function mdCell(val) {
  return String(val ?? '--').replace(/\|/g, '\\|');
}

function mdLink(text, url) {
  const safeText = String(text ?? '').replace(/[[\]|]/g, (c) => `\\${c}`);
  return url ? `[${safeText}](${url})` : mdCell(text);
}

function buildMarkdownTable(headers, rows) {
  const header = `| ${headers.join(' | ')} |`;
  const separator = `| ${headers.map(() => '---').join(' | ')} |`;
  const body = rows.map((cells) => `| ${cells.join(' | ')} |`).join('\n');
  return [header, separator, body].join('\n');
}

// =============================================================================
// Per-operation renderers
// =============================================================================

function Section({ title, children, mt = 0 }) {
  return (
    <Box mt={mt} mb={2}>
      <Heading level={6} mt={0} mb={1}>
        {title}
      </Heading>
      {children}
    </Box>
  );
}

function EmptyResult({ message = 'No matching PeeringDB records were returned.' }) {
  return (
    <Callout intent="none" icon="info-sign">
      <Text>{message}</Text>
    </Callout>
  );
}

function ErrorResult({ message }) {
  return (
    <Callout intent="danger" icon="error" title="PeeringDB query failed">
      <Text>{message}</Text>
    </Callout>
  );
}

// =============================================================================
// Per-operation renderers
// =============================================================================

function renderHasPeeringdbRecord(result) {
  const entries = Object.entries(result || {});
  if (entries.length === 0) {
    return <EmptyResult />;
  }
  return (
    <Markdown>
      {buildMarkdownTable(
        ['ASN', 'Has PeeringDB Record'],
        entries.map(([asn, has]) => [mdLink(`AS${asn}`, asnDetailsUrl(asn)), has ? 'Yes' : 'No'])
      )}
    </Markdown>
  );
}

function AsnProfile({ profile }) {
  const { policy = {}, network_profile: net = {} } = profile;
  return (
    <>
      <Section title="Identity">
        <DataGrid>
          <DataRow label="ASN" value={`AS${profile.asn}`} />
          <DataRow label="Name" value={profile.as_name} />
          <DataRow
            label="PeeringDB"
            value={profile.pdb_url}
            display={<ExternalLink to={profile.pdb_url || pdbAsnUrl(profile.asn)} />}
          />
          <DataRow label="Website" value={profile.website} display={<ExternalLink to={profile.website} />} />
        </DataGrid>
      </Section>

      <Section title="Network Profile">
        <DataGrid>
          <DataRow label="Network Type" value={net.network_type} />
          <DataRow label="Geo Scope" value={net.network_scope} />
          <DataRow label="Traffic Levels" value={net.traffic_info} fallback="Not Disclosed" />
          <DataRow label="Traffic Ratio" value={net.traffic_profile} />
          <DataRow label="IPv4 Prefixes" value={net.ipv4_prefixes} />
          <DataRow label="IPv6 Prefixes" value={net.ipv6_prefixes} />
        </DataGrid>
      </Section>

      <Section title="Peering Policy">
        <DataGrid>
          <DataRow label="Policy Type" value={policy.policy_type} display={<PolicyType value={policy.policy_type} />} />
          <DataRow label="Policy URL" value={policy.policy_url} display={<ExternalLink to={policy.policy_url} />} />
          <DataRow
            label="Multiple Locations"
            value={policy.multiple_locations_needed}
            display={<PolicyLocations value={policy.multiple_locations_needed} />}
          />
          <DataRow label="Contract Required" value={policy.contract_needed} />
        </DataGrid>
      </Section>

      {profile.notes && (
        <Section title="Notes">
          <Text small>{profile.notes}</Text>
        </Section>
      )}

      <Section title="Presence">
        <DataGrid>
          <DataRow label="Internet Exchanges" value={profile.n_ixs ?? 0} />
          <DataRow label="Facilities" value={profile.n_facs ?? 0} />
        </DataGrid>
      </Section>
    </>
  );
}

function renderAsnInfo(result) {
  if (!result) {
    return <EmptyResult message="No PeeringDB record found for this ASN." />;
  }
  return <AsnProfile profile={result} />;
}

function renderAsnSummary(result) {
  if (!result) {
    return <EmptyResult message="No PeeringDB record found for this ASN." />;
  }

  const { ixs = [], facilities = [] } = result;

  return (
    <>
      <AsnProfile profile={result} />

      {ixs.length > 0 && (
        <Section title={`Internet Exchanges (${ixs.length})`} mt={2}>
          <Markdown>
            {buildMarkdownTable(
              ['Exchange', 'Location', 'Members', 'Facilities', 'Capacity (Mbps)'],
              ixs.map((ix) => [
                mdLink(ix.ix_name, ix.pdb_ix_url || pdbIxUrl(ix.pdb_ix_id)),
                mdCell(formatLocation(ix)),
                mdCell(ix.member_count),
                mdCell(ix.fac_count),
                mdCell(ix.total_ix_capacity_mbps)
              ])
            )}
          </Markdown>
        </Section>
      )}

      {facilities.length > 0 && (
        <Section title={`Facilities (${facilities.length})`} mt={2}>
          <Markdown>
            {buildMarkdownTable(
              ['Facility', 'Location', 'Networks', 'IXes'],
              facilities.map((fac) => [
                mdLink(fac.fac_name, fac.pdb_fac_url || pdbFacUrl(fac.pdb_fac_id)),
                mdCell(formatLocation(fac)),
                mdCell(fac.n_asns),
                mdCell(fac.n_ixs)
              ])
            )}
          </Markdown>
        </Section>
      )}
    </>
  );
}

function renderIxDetails(result, query = {}) {
  if (!result) {
    return <EmptyResult message="No PeeringDB exchange found." />;
  }
  const url = pdbIxUrl(query.pdb_ix_id);
  return (
    <>
      <Section title="Exchange">
        <DataGrid>
          <DataRow label="Name" value={result.ix_name} />
          <DataRow label="Location" value={formatLocation(result)} />
          <DataRow label="Address" value={result.address} />
          <DataRow label="Website" value={result.ix_website} display={<ExternalLink to={result.ix_website} />} />
          <DataRow label="PeeringDB" value={url} display={<ExternalLink to={url} />} />
        </DataGrid>
      </Section>
      <Section title="Stats" mt={2}>
        <DataGrid>
          <DataRow label="Member ASNs" value={result.n_member_asns} />
          <DataRow label="Facilities" value={result.n_ix_facilities} />
          <DataRow label="Total Capacity (Mbps)" value={result.total_mbps} />
          <DataRow label="Open Policy Members" value={result.open_policy_member_asns} />
          <DataRow label="IPv6 Capable Members" value={result.ipv6_member_asns} />
          <DataRow label="IPv6 Subnet" value={result.ipv6} />
        </DataGrid>
      </Section>
    </>
  );
}

function renderFacDetails(result, query = {}) {
  if (!result) {
    return <EmptyResult message="No PeeringDB facility found." />;
  }
  const url = pdbFacUrl(query.pdb_fac_id);
  return (
    <>
      <Section title="Facility">
        <DataGrid>
          <DataRow label="Name" value={result.fac_name} />
          <DataRow label="Location" value={formatLocation(result)} />
          <DataRow label="Address" value={result.address} />
          <DataRow label="Website" value={result.fac_website} display={<ExternalLink to={result.fac_website} />} />
          <DataRow label="PeeringDB" value={url} display={<ExternalLink to={url} />} />
        </DataGrid>
      </Section>
      <Section title="Stats" mt={2}>
        <DataGrid>
          <DataRow label="Member ASNs" value={result.n_member_asns} />
          <DataRow label="Internet Exchanges" value={result.n_ixs} />
          <DataRow label="Latitude" value={result.lat} />
          <DataRow label="Longitude" value={result.long} />
        </DataGrid>
      </Section>
    </>
  );
}

function ixTable(rows, { showCandidateCount = false } = {}) {
  if (!rows || rows.length === 0) {
    return <EmptyResult />;
  }
  const headers = ['Exchange', 'Location', 'Members', 'Facilities'];
  if (showCandidateCount) {
    headers.push('Candidate ASNs Present');
  }
  const tableRows = rows.map((ix) => {
    const id = ix.pdb_ix_id || ix.ix_id;
    const url = ix.pdb_ix_url || pdbIxUrl(id);
    const cells = [
      mdLink(ix.ix_name, url),
      mdCell(formatLocation(ix)),
      mdCell(ix.member_count ?? ix.n_member_asns),
      mdCell(ix.fac_count ?? ix.n_ix_facilities ?? ix.n_ix_sites)
    ];
    if (showCandidateCount) {
      cells.push(mdCell(ix.n_candidate_asns));
    }
    return cells;
  });
  return <Markdown>{buildMarkdownTable(headers, tableRows)}</Markdown>;
}

function facTable(rows, { showCandidateCount = false } = {}) {
  if (!rows || rows.length === 0) {
    return <EmptyResult />;
  }
  const headers = ['Facility', 'Location', 'Members', 'IXes'];
  if (showCandidateCount) {
    headers.push('Candidate ASNs Present');
  }
  const tableRows = rows.map((fac) => {
    const id = fac.pdb_fac_id || fac.fac_id;
    const url = fac.pdb_fac_url || pdbFacUrl(id);
    const cells = [
      mdLink(fac.fac_name, url),
      mdCell(formatLocation({ city: fac.fac_city, country: fac.fac_country, continent: fac.fac_continent })),
      mdCell(fac.n_member_asns ?? (Array.isArray(fac.asns_in_fac) ? fac.asns_in_fac.length : null)),
      mdCell(fac.n_ixs)
    ];
    if (showCandidateCount) {
      cells.push(mdCell(fac.n_candidate_asns));
    }
    return cells;
  });
  return <Markdown>{buildMarkdownTable(headers, tableRows)}</Markdown>;
}

function renderIxAtMyFacilities(rows) {
  if (!rows || rows.length === 0) {
    return <EmptyResult />;
  }
  return (
    <>
      {rows.map((fac) => (
        <Box key={fac.fac_id} mb={3}>
          <Heading level={6} mb={1}>
            <a href={fac.pdb_fac_url || pdbFacUrl(fac.fac_id)} target="_blank" rel="noreferrer">
              {fac.name}
            </a>
          </Heading>
          <Text small muted as="div" mb={1}>
            {[fac.fac_address, fac.fac_city, fac.fac_country, fac.fac_continent].filter(Boolean).join(', ')}
          </Text>
          {fac.ixes && fac.ixes.length > 0 ? (
            <Markdown>
              {buildMarkdownTable(
                ['Exchange', 'Members', 'Sites', 'My ASN is a Member'],
                fac.ixes.map((ix) => [
                  mdLink(ix.ix_name, ix.pdb_ix_url || pdbIxUrl(ix.ix_id)),
                  mdCell(ix.n_ix_member_asns),
                  mdCell(ix.n_ix_sites),
                  ix.my_asn_is_member ? 'Yes' : 'No'
                ])
              )}
            </Markdown>
          ) : (
            <Text small muted>
              No exchanges at this facility.
            </Text>
          )}
        </Box>
      ))}
    </>
  );
}

function asnRowTable(rows) {
  if (!rows || rows.length === 0) {
    return <EmptyResult />;
  }
  return (
    <Markdown>
      {buildMarkdownTable(
        ['ASN', 'Network', 'Type', 'Policy', 'Traffic', 'Scope'],
        rows.map((row) => [
          mdLink(`AS${row.asn}`, asnDetailsUrl(row.asn)),
          mdCell(row.as_name),
          mdCell(row.network_type),
          mdCell(row.policy_type),
          mdCell(row.traffic_info),
          mdCell(row.network_scope)
        ])
      )}
    </Markdown>
  );
}

function renderAsnIpsAtIx(rows, query = {}) {
  if (!rows || rows.length === 0) {
    return <EmptyResult message="This ASN has no recorded ports at the requested IX." />;
  }
  return (
    <>
      <Text small muted as="div" mb={2}>
        Ports for AS{query.asn} at PeeringDB IX{' '}
        <a href={pdbIxUrl(query.pdb_ix_id)} target="_blank" rel="noreferrer">
          {query.pdb_ix_id}
        </a>
      </Text>
      <Markdown>
        {buildMarkdownTable(
          ['Port Speed (Mbps)', 'IP Addresses'],
          rows.map((port) => [mdCell(port.port_speed_mbps), mdCell((port.ips || []).join(', '))])
        )}
      </Markdown>
    </>
  );
}

function renderFacListCompact(rows) {
  if (!rows || rows.length === 0) {
    return <EmptyResult />;
  }
  return (
    <Markdown>
      {buildMarkdownTable(
        ['Facility', 'PeeringDB ID'],
        rows.map((fac) => [mdLink(fac.fac_name, pdbFacUrl(fac.pdb_fac_id)), mdCell(fac.pdb_fac_id)])
      )}
    </Markdown>
  );
}

function renderAsnContacts(rows) {
  if (!rows || rows.length === 0) {
    return <EmptyResult message="No public PeeringDB contacts were found for this ASN." />;
  }
  return (
    <Markdown>
      {buildMarkdownTable(
        ['Name', 'Role', 'Email', 'Phone'],
        rows.map((contact) => [
          mdCell(contact.contact_name),
          mdCell(contact.role),
          mdCell(contact.email),
          mdCell(contact.phone)
        ])
      )}
    </Markdown>
  );
}

function renderIxListCompact(rows) {
  if (!rows || rows.length === 0) {
    return <EmptyResult />;
  }
  return (
    <Markdown>
      {buildMarkdownTable(
        ['Exchange', 'PeeringDB ID'],
        rows.map((ix) => [mdLink(ix.ix_name, pdbIxUrl(ix.pdb_ix_id)), mdCell(ix.pdb_ix_id)])
      )}
    </Markdown>
  );
}

// =============================================================================
// Dispatch
// =============================================================================

function renderByOperation(operation, result, query) {
  switch (operation) {
    case 'has_peeringdb_record':
      return renderHasPeeringdbRecord(result);
    case 'get_asn_info':
      return renderAsnInfo(result);
    case 'get_asn_summary':
      return renderAsnSummary(result);
    case 'get_ix_details':
      return renderIxDetails(result, query);
    case 'get_fac_details':
      return renderFacDetails(result, query);
    case 'common_ix':
      return ixTable(result, { showCandidateCount: true });
    case 'list_ix_by_geo':
    case 'list_ix_by_geofence':
    case 'search_ix_by_name':
      return ixTable(result);
    case 'common_facility':
      return facTable(result, { showCandidateCount: true });
    case 'list_fac_by_geo':
    case 'search_fac_by_name':
      return facTable(result);
    case 'ix_at_my_facilities':
      return renderIxAtMyFacilities(result);
    case 'list_asn_for_ix':
    case 'list_asn_for_fac':
      return asnRowTable(result);
    case 'list_fac_for_ix':
      return renderFacListCompact(result);
    case 'list_ix_for_fac':
      return renderIxListCompact(result);
    case 'asn_ips_at_ix':
      return renderAsnIpsAtIx(result, query);
    case 'get_asn_contacts':
      return renderAsnContacts(result);
    default:
      return null;
  }
}

const OPERATION_TITLES = {
  has_peeringdb_record: 'PeeringDB Record Check',
  common_ix: 'Common Internet Exchanges',
  ix_at_my_facilities: 'Exchanges at My Facilities',
  list_ix_by_geo: 'Internet Exchanges by Geography',
  list_ix_by_geofence: 'Internet Exchanges within Radius',
  common_facility: 'Common Facilities',
  get_ix_details: 'Internet Exchange Details',
  get_fac_details: 'Facility Details',
  get_asn_info: 'ASN PeeringDB Profile',
  get_asn_summary: 'ASN PeeringDB Summary',
  search_ix_by_name: 'Internet Exchange Search',
  search_fac_by_name: 'Facility Search',
  list_asn_for_ix: 'ASNs at Internet Exchange',
  list_asn_for_fac: 'ASNs at Facility',
  list_fac_by_geo: 'Facilities by Geography',
  list_fac_for_ix: 'Facilities for Internet Exchange',
  list_ix_for_fac: 'Internet Exchanges for Facility',
  asn_ips_at_ix: 'ASN IPs at Internet Exchange',
  get_asn_contacts: 'ASN PeeringDB Contacts'
};

class PeeringDbToolResult extends Component {
  render() {
    const { tool = {}, onCloseToolResult, isInlineMode } = this.props;
    const { raw_result: rawResult, query_object: queryObject = {} } = tool;
    // The umbrella PeeringDB tool records `operation` on the query object. The
    // PeeringDB sub-agent instead exposes one tool per operation, so fall back to
    // the tool name (which matches the operation keys) for those child calls.
    const operation = queryObject.operation || tool.tool_name;
    const title = tool.title || OPERATION_TITLES[operation] || 'PeeringDB Result';

    let body;
    if (rawResult && typeof rawResult === 'object' && rawResult.error) {
      body = <ErrorResult message={String(rawResult.error)} />;
    } else if (!operation) {
      body = <EmptyResult message="No PeeringDB operation was recorded for this tool call." />;
    } else {
      body = renderByOperation(operation, rawResult, queryObject) ?? (
        <EmptyResult message={`Unsupported PeeringDB operation: ${operation}`} />
      );
    }

    return (
      <ToolResultCard
        tool={tool}
        onCloseToolResult={onCloseToolResult}
        isInlineMode={isInlineMode}
        className="tool-result-peeringdb"
        title={title}
        minHeight={isInlineMode ? 200 : 450}
        overflow="hidden"
      >
        <Box flex={1} overflow="auto" px={2} pb={2}>
          {body}
        </Box>
      </ToolResultCard>
    );
  }
}

export default PeeringDbToolResult;
